import { cloneElement, createElement, ReactElement, ReactNode } from "react";
import cx from "classnames";
import nextStyles from "./Text.module.css";
import enterpriseStyles from "./TextEnterprise.module.css";
import { useTextVariant } from "../ThemeProvider/useTextVariant";

export type Size =
  | "hero-header"
  | "display-header"
  | "sub-hero-header"
  | "header1"
  | "header2"
  | "header3"
  | "header4"
  | "header5"
  | "body1"
  | "body2"
  | "body3"
  | "body4"
  | "body5";
type Element =
  | "span"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "label"
  | "li"
  | ReactElement;
export type Weight = "normal" | "bold" | "semibold" | "extrabold" | "medium";
type TextAlign = "left" | "right" | "center";
export type Color =
  | "primary"
  | "secondary"
  | "placeholder"
  | "interactive"
  | "interactive-secondary"
  | "white"
  | "white-secondary"
  | "positive"
  | "negative"
  | "caution"
  | "negative-emphasis"
  | "brand"
  | "grey"
  | "custom";
type Display = "block" | "inline" | "list-item";
export type Decoration =
  | "none"
  | "underline"
  | "italic"
  | "strikethrough"
  | "uppercase";

function getElementForSize(size: Size): Element {
  switch (size) {
    case "hero-header":
    case "display-header":
    case "sub-hero-header":
    case "header1":
      return "h1";
    case "header2":
      return "h2";
    case "header3":
      return "h3";
    case "header4":
      return "h4";
    case "header5":
      return "h5";
    case "body1":
    case "body2":
    case "body3":
    case "body4":
    case "body5":
      return "span";
  }
}

export interface TextProps {
  color?: Color;
  display?: Display;
  size: Size;
  textAlign?: TextAlign;
  weight?: Weight;
  children?: ReactNode | undefined;
  screenReaderOnly?: boolean;
  element?: Element;
  htmlFor?: string;
  className?: string;
  decoration?: Decoration;
  id?: string;
}

export default function Text({
  size,
  children,
  weight,
  textAlign = "left",
  color = "primary",
  display = "inline",
  screenReaderOnly = false,
  element,
  htmlFor,
  className,
  decoration,
  id,
}: TextProps) {
  const textVariant = useTextVariant();
  const styles = textVariant === "enterprise" ? enterpriseStyles : nextStyles;
  const elementProps = {
    id,
    htmlFor,
    className: cx(className, {
      [styles.displayHeader]: size === "display-header",
      [styles.heroHeader]: size === "hero-header",
      [styles.subHeroHeader]: size === "sub-hero-header",
      [styles.header1]: size === "header1",
      [styles.header2]: size === "header2",
      [styles.header3]: size === "header3",
      [styles.header4]: size === "header4",
      [styles.header5]: size === "header5",
      [styles.body1]: size === "body1",
      [styles.body2]: size === "body2",
      [styles.body3]: size === "body3",
      [styles.body4]: size === "body4",
      [styles.body5]: size === "body5",
      [styles.displayInline]: display === "inline",
      [styles.displayBlock]: display === "block",
      [styles.displayListItem]: display === "list-item",
      [styles.weightNormal]: weight === "normal",
      [styles.weightMedium]: weight === "medium",
      [styles.weightBold]: weight === "bold",
      [styles.weightExtraBold]: weight === "extrabold",
      [styles.weightSemiBold]: weight === "semibold",
      [styles.weightMedium]: weight === "medium",
      [styles.textAlignLeft]: textAlign === "left",
      [styles.textAlignRight]: textAlign === "right",
      [styles.textAlignCenter]: textAlign === "center",
      [styles.colorPrimary]: color === "primary",
      [styles.colorSecondary]: color === "secondary",
      [styles.colorPlaceholder]: color === "placeholder",
      [styles.colorInteractive]: color === "interactive",
      [styles.colorInteractiveSecondary]: color === "interactive-secondary",
      [styles.colorWhite]: color === "white",
      [styles.colorWhiteSecondary]: color === "white-secondary",
      [styles.colorPositive]: color === "positive",
      [styles.colorNegative]: color === "negative",
      [styles.colorCaution]: color === "caution",
      [styles.colorNegativeEmphasis]: color === "negative-emphasis",
      [styles.colorViolet]: color === "brand",
      [styles.decorationUnderline]: decoration === "underline",
      [styles.decorationItalic]: decoration === "italic",
      [styles.decorationStrikethrough]: decoration === "strikethrough",
      [styles.decorationUppercase]: decoration === "uppercase",
      "sr-only": screenReaderOnly,
    }),
    children,
  };

  const computedElement = element || getElementForSize(size);
  return typeof computedElement === "string"
    ? createElement(computedElement, elementProps)
    : cloneElement(computedElement, elementProps);
}
